import { pickT } from "@repo/shared";
import type { FlowRequirementIssue } from "@repo/shared/types";
import { AlertTriangle } from "lucide-react";
import { getFlowComponentLabel } from "@/components/flow/flow-component-types";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import type { ComponentType } from "@/lib/types/flow";
import { T } from "./requirement-issues-alert.i18n";

type RequirementIssuesAlertProps = {
  issues: FlowRequirementIssue[] | undefined;
  /** The flow's language, which the rest of the flow editor uses for its own text. */
  language: string;
  /** Name the page after each component. Off in the page editor, where it is this page. */
  showPages: boolean;
};

/**
 * PR 546 — warns about components the flow's assignments don't support (e.g. a product
 * assessment on a flow without a portfolio collection) before Activate refuses the flow.
 * The server computes the issues with the same evaluation Activate runs.
 */
export function RequirementIssuesAlert({
  issues,
  language,
  showPages,
}: RequirementIssuesAlertProps) {
  const t = pickT(T, language);
  const visible = (issues ?? []).filter((issue) => issue.components.length > 0);
  if (visible.length === 0) return null;

  return (
    <Alert variant="warning">
      <AlertTriangle />
      <AlertTitle>{t.title}</AlertTitle>
      <AlertDescription>
        <ul className="list-disc space-y-1 pl-4">
          {visible.map((issue) => {
            const names = issue.components.map((c) => {
              const label = getFlowComponentLabel(c.componentType as ComponentType, language);
              return showPages ? `${label} ${t.onPage} "${c.pageName}"` : label;
            });
            return (
              <li key={issue.kind}>
                {t.kinds[issue.kind].needs}: {[...new Set(names)].join(", ")}.{" "}
                {t.kinds[issue.kind].fix}
              </li>
            );
          })}
        </ul>
        <p className="mt-2">{t.activation}</p>
      </AlertDescription>
    </Alert>
  );
}
